/**
 * The three views `/fly` can be flown from, and how a key resolves to one.
 *
 * Pure data and a lookup — no renderer, no scene — so `flight-scene.ts` keeps
 * only the frame loop, and a route can read the table without pulling three.js
 * into its import graph.
 */

import type { DiscreteCommand } from "./keys.ts";

/**
 * `cockpit` is the spawn default (`flight-scene.ts`'s `createFlightScene`) — the original's
 * primary way to fly, per `docs/game/manual/04-gameplay.md:673`'s cockpit
 * forward/left/right/back views. `chase`/`flyBy` are the port's external
 * views, still reachable on 1/2 (or by cycling with C).
 */
export type CameraMode = "cockpit" | "chase" | "flyBy";

/** Cycle order for the `C` key / `cameraCycle` command — the order `docs/game/ui-menus.md`'s port-notes appendix lists the three views in. Also the spawn order: `DEFAULT_CAMERA_MODE` is its first entry. */
const CAMERA_CYCLE: readonly CameraMode[] = ["cockpit", "chase", "flyBy"];

/**
 * The view `/fly` spawns into — the original's own primary view
 * (`docs/game/manual/04-gameplay.md:673`). Briefly demoted to `chase` after
 * the first cockpit attempt stretched a single 192-px `DASH` fragment across
 * the window and misrendered (user report, 2026-08-19); restored here now
 * that the panel is composed at 1:1 from each aircraft's own `.TTM` draw
 * program (`cockpit-composition.ts`).
 */
export const DEFAULT_CAMERA_MODE: CameraMode = "cockpit";

export function nextCameraMode(current: CameraMode): CameraMode {
  const index = CAMERA_CYCLE.indexOf(current);
  return CAMERA_CYCLE[(index + 1) % CAMERA_CYCLE.length]!;
}

/**
 * What `Fly.tsx`'s camera-related `DiscreteCommand`s resolve to, as a lookup
 * rather than an if-chain — `current` only matters for `cameraCycle`.
 * Returns `undefined` for a command this table doesn't own (`pause`,
 * `muteAudio`), so the route can fall through to its own handling for those.
 */
export function cameraModeForCommand(command: DiscreteCommand, current: CameraMode): CameraMode | undefined {
  switch (command) {
    case "cameraChase":
      return "chase";
    case "cameraFlyBy":
      return "flyBy";
    case "cameraCockpit":
      return "cockpit";
    case "cameraCycle":
      return nextCameraMode(current);
    default:
      return undefined;
  }
}

